import { useCallback, useEffect, useMemo, useState } from "react";
import { useGame } from "@/context/GameContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ITEM_ICON, itemName, SKILL_LABEL, skillIcon } from "@/lib/gameData";
import {
  Skull, Sparkles, Coins, Package, ChevronRight, Heart, Swords, Trophy, StopCircle,
} from "lucide-react";

function RewardChips({ reward, contentItems }) {
  if (!reward) return null;
  const chips = [];
  if (reward.gold) chips.push({ icon: Coins, label: `${reward.gold} gp` });
  for (const [sk, amt] of Object.entries(reward.xp || {})) {
    chips.push({ icon: Sparkles, label: `+${amt} ${SKILL_LABEL[sk] || sk} XP` });
  }
  for (const [iid, iq] of reward.items || []) {
    chips.push({ icon: Package, label: `${iq}× ${itemName(iid, contentItems)}` });
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => {
        const Icon = c.icon;
        return (
          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-900/70 bg-amber-950/40 text-amber-200 font-numeric text-[10px]">
            <Icon size={11} /> {c.label}
          </span>
        );
      })}
    </div>
  );
}

function ActiveRunCard({ active, onAbandon, snap, content }) {
  const dg = content?.dungeons?.[active.id];
  if (!dg) return null;
  const target = active.target_monster ? content?.monsters?.[active.target_monster] : null;
  const pct = target && snap?.combat ? Math.max(0, (snap.combat.monster_hp / target.hp) * 100) : 0;

  return (
    <div className="panel p-4" data-testid="active-dungeon-card">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div>
          <div className="font-numeric text-[10px] uppercase tracking-widest text-amber-500">Current run</div>
          <h2 className="font-display text-2xl heading-gilded leading-tight">{active.dungeon_name}</h2>
        </div>
        <button
          onClick={onAbandon}
          className="btn-ghost-gold text-[10px] px-2 py-1 flex items-center gap-1"
          data-testid="abandon-dungeon-btn"
        >
          <StopCircle size={12} /> Abandon run
        </button>
      </div>

      {/* Wave dots */}
      <div className="flex items-center gap-2 mb-3">
        {Array.from({ length: active.total_waves }).map((_, i) => (
          <span
            key={i}
            className={
              "w-6 h-2 rounded-full " +
              (i < active.wave      ? "bg-emerald-700"
              : i === active.wave    ? "bg-amber-400 pulse-tick"
              :                        "bg-stone-800")
            }
          />
        ))}
        <span
          className={
            "ml-1 text-[10px] font-numeric uppercase tracking-widest " +
            (active.on_boss ? "text-red-300" : "text-stone-500")
          }
        >
          {active.on_boss ? "Boss" : `Wave ${active.wave + 1}/${active.total_waves}`}
        </span>
      </div>

      {/* Current target */}
      {target && (
        <div className="panel-inset p-3 flex items-center gap-3" data-testid="dungeon-target">
          <div className="w-12 h-12 rounded panel-inset flex items-center justify-center">
            <Skull size={20} className="text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base text-stone-100 truncate">{target.name}</div>
            <div className="text-[10px] font-numeric text-stone-500">
              CB {target.combat_level} · HP {target.hp} · Max {target.max_hit}
            </div>
            <div className="hp-bar-outer mt-1">
              <div className="hp-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[10px] font-numeric text-red-300 mt-1">
              HP {snap?.combat?.monster_hp ?? target.hp} / {target.hp}
              {active.on_boss && <span className="ml-2 text-amber-400">· Boss encounter</span>}
              {!active.on_boss && <span className="ml-2">· {active.wave_remaining} left in wave</span>}
            </div>
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="font-numeric text-[10px] uppercase tracking-widest text-amber-500 mb-1.5">Chest on completion</div>
        <RewardChips reward={dg.reward} contentItems={content?.items || {}} />
      </div>
    </div>
  );
}

function DungeonListingCard({ dungeon, onEnter, canEnter, snap }) {
  const cb = useMemo(() => {
    const s = snap?.skills || {};
    const g = (k) => s[k]?.level ?? 1;
    const base = 0.25 * (g("defence") + g("hitpoints") + Math.floor(g("prayer") / 2));
    const melee = 0.325 * (g("attack") + g("strength"));
    return Math.floor(base + melee);
  }, [snap?.skills]);
  const locked = cb < dungeon.min_combat_level;

  return (
    <article
      className="panel p-4 space-y-3"
      data-testid={`dungeon-card-${dungeon.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-numeric text-[10px] uppercase tracking-widest text-amber-500">
            {dungeon.region}
          </div>
          <h3 className="font-display text-xl heading-gilded leading-tight">{dungeon.name}</h3>
        </div>
        <span
          className={
            "text-[10px] font-numeric uppercase tracking-widest border rounded px-1.5 py-0.5 " +
            (locked
              ? "bg-stone-900 border-stone-700 text-stone-500"
              : "bg-emerald-900/40 border-emerald-700 text-emerald-300")
          }
        >
          CB {dungeon.min_combat_level}+
        </span>
      </div>
      <p className="text-[12px] italic text-stone-400 font-body leading-snug">&ldquo;{dungeon.flavor}&rdquo;</p>

      <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-numeric text-stone-400">
        <span className="flex items-center gap-1"><Swords size={11} className="text-red-400" /> {dungeon.waves.length} waves</span>
        <span className="text-stone-600">·</span>
        <span className="flex items-center gap-1"><Trophy size={11} className="text-amber-400" /> boss</span>
      </div>

      <div>
        <div className="font-numeric text-[10px] uppercase tracking-widest text-amber-500 mb-1.5">Chest</div>
        <RewardChips reward={dungeon.reward} contentItems={{}} />
      </div>

      <button
        onClick={() => onEnter(dungeon.id)}
        disabled={locked || !canEnter}
        className={
          "w-full text-xs py-2 rounded border font-body transition-colors duration-200 " +
          (locked
            ? "bg-stone-900 border-stone-800 text-stone-500 cursor-not-allowed"
            : !canEnter
              ? "bg-stone-900 border-stone-700 text-stone-500 cursor-not-allowed"
              : "bg-amber-800 hover:bg-amber-700 border-amber-600 text-amber-50")
        }
        data-testid={`enter-dungeon-${dungeon.id}`}
      >
        {locked ? `Locked · needs CB ${dungeon.min_combat_level}` : canEnter ? "Enter dungeon" : "Finish current run first"}
      </button>
    </article>
  );
}

function SharedNodeBar({ node }) {
  const pct = Math.round((node.remaining / node.capacity) * 100);
  const depleted = node.remaining <= 0;
  return (
    <div className="panel-inset p-2.5 flex items-center gap-3" data-testid={`node-${node.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display text-xs text-stone-100 truncate">{node.name}</div>
          <div className={"text-[10px] font-numeric " + (depleted ? "text-red-300" : "text-amber-300")}>
            {node.remaining}/{node.capacity}
          </div>
        </div>
        <div className="xp-bar-outer" style={{ height: 4 }}>
          <div
            className="xp-bar-fill"
            style={{
              width: `${pct}%`,
              background: depleted ? "linear-gradient(180deg, #ef4444 0%, #7f1d1d 100%)" : undefined,
            }}
          />
        </div>
        <div className="text-[9px] font-numeric text-stone-500 mt-1 flex justify-between">
          <span>{node.region}</span>
          <span>{depleted ? `Respawn in ${node.respawn_in}s` : `Regen · ${node.respawn_sec}s`}</span>
        </div>
      </div>
    </div>
  );
}

export function DungeonsPanel() {
  const { snap, content, refresh: refreshGame, worldNodes } = useGame();
  const [data, setData] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [d, w] = await Promise.all([
        api.get("/api/game/dungeons"),
        api.get("/api/game/world/state"),
      ]);
      setData(d.data);
      setNodes(w.data.nodes);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const refreshDungeonsOnly = useCallback(async () => {
    try {
      const d = await api.get("/api/game/dungeons");
      setData(d.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    // One-time initial fetch (dungeons + fallback world snapshot).
    // After this, world-node counts are pushed live over the WebSocket
    // (see GameContext -> worldNodes) so we don't poll.
    refresh();
    // Dungeons state (active party progress, wave timers) still refreshes
    // periodically since it's per-player, not global.
    const t = setInterval(refreshDungeonsOnly, 3000);
    return () => clearInterval(t);
  }, [refresh, refreshDungeonsOnly]);

  // Live-sync shared-node pool counts pushed via WebSocket.
  useEffect(() => {
    if (worldNodes && Array.isArray(worldNodes)) setNodes(worldNodes);
  }, [worldNodes]);

  async function enter(dungeon_id) {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/api/game/dungeons/enter", { dungeon_id });
      toast.success("You step into the dungeon.");
      refresh();
      if (refreshGame) refreshGame();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  async function abandon() {
    try {
      await api.post("/api/game/dungeons/abandon", {});
      toast.message("Withdrew from the dungeon.");
      refresh();
      if (refreshGame) refreshGame();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!data) {
    return <div className="p-6 text-stone-500 italic font-body">Peering into the depths…</div>;
  }

  const dungeons = Object.values(data.dungeons);
  const active = data.active;

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="dungeons-panel">
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Skull size={32} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">Dungeons</h1>
            <p className="text-stone-400 font-body italic text-sm">
              Instanced multi-wave runs. Complete every wave and the boss to claim the chest.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Active run column */}
        <div className="lg:col-span-5 space-y-3">
          {active ? (
            <ActiveRunCard active={active} snap={snap} content={content} onAbandon={abandon} />
          ) : (
            <div className="panel p-6 text-center text-stone-500 italic font-body text-sm" data-testid="no-active-dungeon">
              You are not in a dungeon. Pick one to begin.
            </div>
          )}

          <section className="panel p-3">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2 flex items-center gap-1.5">
              <Heart size={11} className="text-emerald-400" /> Shared nodes · live
            </h3>
            <p className="text-[11px] italic text-stone-500 font-body mb-2">
              Every wanderer draws from the same pools. Nodes deplete and regenerate in real time.
            </p>
            <div className="space-y-1.5" data-testid="shared-nodes-list">
              {nodes.map((n) => <SharedNodeBar key={n.id} node={n} />)}
            </div>
          </section>
        </div>

        {/* Dungeon listings */}
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-3">
          {dungeons.map((dg) => (
            <DungeonListingCard
              key={dg.id}
              dungeon={dg}
              onEnter={enter}
              canEnter={!active}
              snap={snap}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
